import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { pendingInvitesKey } from "../queryKeys";
import type { PendingBreakfastInvite } from "../types";

/**
 * Returns every event where the current user is an attendee with
 * `status = 'pending'` — i.e. invites that have NOT yet been accepted or
 * declined. These are intentionally hidden from the main calendar grid (see
 * the visibility rule in `useBreakfastEvents`) so we need a dedicated
 * surface — the RSVP Center — where guests can actually act on them.
 *
 * Shape matches `BreakfastEventWithAttendees` plus an embedded `host`
 * profile so the card can render "Hosted by <name>" without a second
 * round-trip.
 */
export function usePendingBreakfastInvites(userId?: string) {
  return useQuery({
    queryKey: pendingInvitesKey(userId),
    enabled: !!userId,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<PendingBreakfastInvite[]> => {
      if (!userId) return [];

      // Two-step fetch: first find attendee rows that belong to me and are
      // still pending, then hydrate the event with host + full attendee /
      // potluck arrays so the card has everything it needs.
      const { data: pendingRows, error: pendErr } = await supabase
        .from("breakfast_attendees")
        .select("event_id")
        .eq("user_id", userId)
        .eq("status", "pending");

      if (pendErr) throw pendErr;
      const eventIds = (pendingRows ?? []).map(
        (r: { event_id: string }) => r.event_id,
      );
      if (eventIds.length === 0) return [];

      const { data, error } = await supabase
        .from("breakfast_events")
        .select(`
          *,
          host:profiles!breakfast_events_host_id_fkey(
            first_name, last_name, email, avatar_url
          ),
          attendees:breakfast_attendees(
            id, event_id, user_id, status, bring_item,
            profiles(first_name, last_name, avatar_url, email)
          ),
          potluck_items(
            id, event_id, item_name, claimed_by, created_at,
            claimer:profiles(first_name, last_name, email)
          )
        `)
        .in("id", eventIds)
        .eq("status", "active")
        .order("event_date", { ascending: true })
        .order("event_time", { ascending: true });

      if (error) throw error;
      return (data as unknown) as PendingBreakfastInvite[];
    },
  });
}
